import React, { useContext, useEffect, useState } from 'react';
import {
  Card, Col, Row, Typography, Button, Badge, Tooltip,
  Input, Checkbox, Tag, Divider, Grid,
} from 'antd';
import { mqtt, iot } from 'aws-iot-device-sdk-v2';
import { SimulatorContext } from 'store/context';
import {
  CarOutlined, WarningOutlined, UnlockOutlined,
  CompassOutlined, ControlOutlined, IdcardOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;


const SectionLabel = ({ children }) => (
  <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
    {children}
  </span>
);

const FieldRow = ({ label, children }) => (
  <div>
    <SectionLabel>{label}</SectionLabel>
    <div style={{ marginTop: 6 }}>{children}</div>
  </div>
);

const CheckRow = ({ checked, onChange, children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      borderRadius: 8,
      background: checked ? '#f0fdf4' : '#f8fafc',
      border: `1px solid ${checked ? '#86efac' : '#e2e8f0'}`,
      transition: 'all 0.2s',
      cursor: 'pointer',
    }}
    onClick={() => onChange({ target: { checked: !checked } })}
  >
    <Text style={{ fontSize: 13, color: checked ? '#166534' : '#64748b', fontWeight: 500 }}>{children}</Text>
    <Checkbox checked={checked} onChange={onChange} onClick={(e) => e.stopPropagation()} />
  </div>
);


const ValetMode = () => {
  const {
    stsToken: credentials,
    vehicleSimulatorSettings: { serialNo },
  } = useContext(SimulatorContext);

  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [logMessages, setLogMessages] = useState([]);
  const [valetStatus, setValetStatus] = useState('fetching...');
  const [sendingValetMode, setSendingValetMode] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const [pin, setPin] = useState('1234');
  const [maxSpeed, setMaxSpeed] = useState(50);
  const [geofenceEnabled, setGeofenceEnabled] = useState(true);
  const [trunkLocked, setTrunkLocked] = useState(true);
  const [gloveboxLocked, setGloveboxLocked] = useState(true);

  const addLog = (msg) =>
    setLogMessages((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev]);

  const subscribeToShadow = (conn) => {
    if (!conn) return;
    conn.subscribe(`$aws/things/${serialNo}/shadow/get/accepted`, mqtt.QoS.AtLeastOnce, (_, payload) => {
      const msg = JSON.parse(new TextDecoder('utf8').decode(new Uint8Array(payload)));
      const active = msg?.state?.reported?.valetMode?.enabled ?? msg?.state?.desired?.valetMode?.enabled;
      setValetStatus(active ? 'Active' : 'Inactive');
      addLog(`Shadow Get: Valet mode is ${active ? 'ON' : 'OFF'}`);
    }).then(() => conn.publish(`$aws/things/${serialNo}/shadow/get`, '{}', mqtt.QoS.AtLeastOnce));

    conn.subscribe(`$aws/things/${serialNo}/shadow/update/accepted`, mqtt.QoS.AtLeastOnce, (_, payload) => {
      const msg = JSON.parse(new TextDecoder('utf8').decode(new Uint8Array(payload)));
      if (msg?.state?.reported?.valetMode !== undefined || msg?.state?.desired?.valetMode !== undefined) {
        const active = msg?.state?.reported?.valetMode?.enabled ?? msg?.state?.desired?.valetMode?.enabled;
        setValetStatus(active ? 'Active' : 'Inactive');
        addLog(`Shadow Update: Valet mode changed to ${active ? 'ON' : 'OFF'}`);
      }
    });
  };

  const fetchValetStatus = async () => {
    try {
      const res = await axios.get(`http://localhost:3500/companionapp/vehicle/${serialNo}/valet/status`);
      const active = res.data?.data?.enabled;
      setValetStatus(active ? 'Active' : 'Inactive');
      addLog(`Loaded initial status from API: ${active ? 'ON' : 'OFF'}`);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 400) {
        setValetStatus('Inactive');
        addLog('Loaded initial status from API: OFF (Not Configured)');
      } else {
        addLog(`Failed to load initial status: ${e.message}`);
      }
    }
  };

  const publishSimulatedViolation = async (violationType, actualVal, thresholdVal) => {
    if (!connection || !isConnected) { addLog('Cannot simulate violation: Not connected to AWS IoT.'); return; }
    try {
      setSendingValetMode(true);
      const payload = {
        serialNo,
        timestamp: new Date().toISOString(),
        category: 'VALET VIOLATION',
        data: { type: violationType, actual: actualVal, threshold: thresholdVal, severity: 'HIGH' },
      };
      const topic = `telematics/nextdev/vehicles/${serialNo}/alert`;
      addLog(`Publishing violation to ${topic}...`);
      await connection.publish(topic, JSON.stringify(payload), mqtt.QoS.AtLeastOnce);
      addLog(`Successfully published [${violationType}] violation event.`);
    } catch (e) {
      addLog(`Failed to publish violation: ${e.message}`);
    } finally {
      setSendingValetMode(false);
    }
  };

  const handleActivate = async () => {
    try {
      setIsActivating(true);
      addLog('Sending Activate Valet Mode API request...');
      const res = await axios.post(
        `http://localhost:3500/companionapp/vehicle/${serialNo}/valet/activate`,
        { pin, maxSpeed: Number(maxSpeed), geofenceEnabled, trunkLocked, gloveboxLocked }
      );
      setValetStatus('Active');
      addLog(`Activation Response: ${res.data.message}`);
    } catch (e) {
      if (e.response?.status === 409) {
        setValetStatus('Active');
        addLog('Valet Mode is already active (409 Conflict). Treating as Active.');
      } else {
        addLog(`Activation Error: ${e?.response?.data?.message || e.message}`);
      }
    } finally {
      setIsActivating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      addLog('Sending Deactivate Valet Mode API request...');
      const res = await axios.post(`http://localhost:3500/companionapp/vehicle/${serialNo}/valet/deactivate`, { pin });
      setValetStatus('Inactive');
      addLog(`Deactivation Response: ${res.data.message}`);
    } catch (e) {
      addLog(`Deactivation Error: ${e?.response?.data?.message || e.message}`);
    } finally {
      setIsDeactivating(false);
    }
  };

  useEffect(() => {
    if (serialNo) fetchValetStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNo]);

  useEffect(() => {
    let activeConnection = null;
    const setupIoT = async () => {
      if (!serialNo || !credentials?.accessKeyId) return;
      try {
        addLog('Connecting to AWS IoT WebSocket...');
        const config = iot.AwsIotMqttConnectionConfigBuilder.new_builder_for_websocket()
          .with_clean_session(true)
          .with_client_id('valet-sim-' + Math.floor(Math.random() * 100000000))
          .with_endpoint('a3p0q0u1ruwh8a-ats.iot.us-east-2.amazonaws.com')
          .with_credentials('us-east-2', credentials.accessKeyId, credentials.secretAccessKey, credentials.sessionToken)
          .with_keep_alive_seconds(30)
          .build();
        const client = new mqtt.MqttClient();
        activeConnection = client.new_connection(config);
        activeConnection.on('connect', () => {
          setIsConnected(true);
          addLog('Connected to AWS IoT Core securely');
          subscribeToShadow(activeConnection);
        });
        activeConnection.on('error', (err) => { setIsConnected(false); addLog(`Connection Error: ${err}`); });
        activeConnection.on('disconnect', () => { setIsConnected(false); addLog('Disconnected from AWS IoT Core'); });
        await activeConnection.connect();
        setConnection(activeConnection);
      } catch (e) {
        addLog(`Failed to connect: ${e.message}`);
      }
    };
    setupIoT();
    return () => { if (activeConnection) activeConnection.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNo, credentials?.accessKeyId]);

  const isActive = valetStatus === 'Active';
  const isFetching = valetStatus === 'fetching...';

  return (
    <>
      {/* page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: '#f0fdfa', border: '1px solid #99f6e4',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <IdcardOutlined style={{ fontSize: 18, color: '#0f766e' }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Valet Mode</Title>
            {serialNo && <Text style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{serialNo}</Text>}
          </div>
        </div>
        <Tooltip title={isConnected ? 'IoT Connected' : 'IoT Disconnected'} color="#1e293b">
          <Badge dot color={isConnected ? 'green' : 'red'}>
            <Tag style={{ borderRadius: 20, fontSize: 11, padding: '3px 10px', cursor: 'default' }}>
              {isConnected ? 'IoT Connected' : 'IoT Disconnected'}
            </Tag>
          </Badge>
        </Tooltip>
      </div>

      <Row gutter={[16, 16]}>

        {/* ── left column ── */}
        <Col xs={24} lg={12}>
          <Row gutter={[0, 16]}>

            {/* status card */}
            <Col xs={24}>
              <Card
                style={{ borderRadius: 12, border: `1px solid ${isActive ? '#fca5a5' : '#e2e8f0'}`, background: isActive ? '#fff5f5' : '#ffffff' }}
                styles={{ body: { padding: '16px 20px' } }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <SectionLabel>Current Status</SectionLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: isFetching ? '#94a3b8' : isActive ? '#ef4444' : '#10b981',
                        boxShadow: isActive ? '0 0 0 3px rgba(239,68,68,0.2)' : isFetching ? 'none' : '0 0 0 3px rgba(16,185,129,0.2)',
                      }} />
                      <Text style={{ fontSize: 22, fontWeight: 700, color: isFetching ? '#94a3b8' : isActive ? '#dc2626' : '#059669' }}>
                        {valetStatus}
                      </Text>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!isActive ? (
                      <Button type="primary" onClick={handleActivate} loading={isActivating} size="large">
                        Activate
                      </Button>
                    ) : (
                      <Button danger type="primary" onClick={handleDeactivate} loading={isDeactivating} size="large">
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>

                {isActive && (
                  <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fca5a5', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <WarningOutlined style={{ color: '#dc2626', fontSize: 14, flexShrink: 0 }} />
                    <Text style={{ fontSize: 12, color: '#dc2626' }}>
                      Valet Mode is active — max speed {maxSpeed} km/h enforced
                    </Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* configuration card */}
            <Col xs={24}>
              <Card
                style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                styles={{ body: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 } }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ControlOutlined style={{ color: '#0f766e' }} />
                    <span>Configuration</span>
                  </div>
                }
              >
                <FieldRow label="Valet PIN">
                  <Input.Password
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter PIN"
                    style={{ maxWidth: 180 }}
                  />
                </FieldRow>

                <FieldRow label="Max Speed">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Input
                      type="number"
                      value={maxSpeed}
                      onChange={(e) => setMaxSpeed(Number(e.target.value))}
                      style={{ maxWidth: 100 }}
                    />
                    <Text style={{ fontSize: 12, color: '#94a3b8' }}>km/h</Text>
                  </div>
                </FieldRow>

                <Divider style={{ margin: '4px 0' }} />

                <FieldRow label="Restrictions">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <CheckRow checked={geofenceEnabled} onChange={(e) => setGeofenceEnabled(e.target.checked)}>
                      Enable 2-Mile Geofence
                    </CheckRow>
                    <CheckRow checked={trunkLocked} onChange={(e) => setTrunkLocked(e.target.checked)}>
                      Lock Trunk External Access
                    </CheckRow>
                    <CheckRow checked={gloveboxLocked} onChange={(e) => setGloveboxLocked(e.target.checked)}>
                      Lock Glovebox Electronically
                    </CheckRow>
                  </div>
                </FieldRow>
              </Card>
            </Col>

            {/* violations card */}
            <Col xs={24}>
              <Card
                style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                styles={{ body: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 } }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CarOutlined style={{ color: '#dc2626' }} />
                    <span>Simulate Violations</span>
                  </div>
                }
              >
                <Text style={{ fontSize: 12, color: '#64748b' }}>
                  Publish mock telemetry to simulate the valet violating configured constraints.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {[
                    { icon: <WarningOutlined />, label: 'Speed Limit Exceeded (65 km/h)', type: 'VALET_SPEED_VIOLATION', actual: 65, threshold: 50 },
                    { icon: <CompassOutlined />, label: 'Geofence Exit', type: 'VALET_GEOFENCE_VIOLATION', actual: 'Outside Zone', threshold: 'Within 2 Miles' },
                    { icon: <UnlockOutlined />, label: 'Trunk Opened', type: 'VALET_TRUNK_OPEN_VIOLATION', actual: 'Unlocked', threshold: 'Locked' },
                  ].map(({ icon, label, type, actual, threshold }) => (
                    <Button
                      key={type}
                      type="primary"
                      danger
                      icon={icon}
                      loading={sendingValetMode}
                      disabled={!isConnected}
                      onClick={() => publishSimulatedViolation(type, actual, threshold)}
                      style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </Card>
            </Col>

          </Row>
        </Col>

        {/* ── right column: logs ── */}
        <Col xs={24} lg={12}>
          <Card
            style={{ borderRadius: 12, border: '1px solid #e2e8f0', height: '100%' }}
            styles={{ body: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' } }}
            title="Live Logs"
            extra={
              logMessages.length > 0 && (
                <Tag color="blue" style={{ borderRadius: 20, fontSize: 10 }}>{logMessages.length}</Tag>
              )
            }
          >
            {/* MQTT topics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                `$aws/things/${serialNo}/shadow/get/accepted`,
                `$aws/things/${serialNo}/shadow/update/accepted`,
              ].map((topic) => (
                <div key={topic} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Badge dot color={isConnected ? 'green' : 'red'} />
                  <Text code style={{ fontSize: 10, wordBreak: 'break-all' }}>{topic}</Text>
                </div>
              ))}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            {/* log messages */}
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: 480, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {logMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
                  No log entries yet
                </div>
              ) : (
                logMessages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: i === 0 ? '#f0f9ff' : '#f8fafc',
                      border: `1px solid ${i === 0 ? '#bfdbfe' : '#e2e8f0'}`,
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: '#475569',
                      wordBreak: 'break-all',
                      lineHeight: 1.5,
                    }}
                  >
                    {msg}
                  </div>
                ))
              )}
            </div>
          </Card>
        </Col>

      </Row>
    </>
  );
};

export default ValetMode;
